'use client';
import { BaseLineRight } from '@/app/components/icons/BaseLineRight';
import { UserAvatar } from '@/app/components/shared/UserAvatar';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/app/components/ui/hover-card';
import { useClawAccess } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { UserInfo } from '@/app/types';
import { cn } from '@/app/utils';
import { NavUserPopover } from './NavUserPopover';
import { UserTypeTag } from './UserTypeTag';

interface NavUserCardProps {
  userInfo: UserInfo;
}

export const NavUserCard = ({ userInfo }: NavUserCardProps) => {
  const { clawPass } = useStore((state) => ({
    clawPass: state.clawPass,
  }));

  const { access } = useClawAccess();

  const triggerContent = (
    <div
      className={cn(
        'flex h-14 items-center gap-[10px] rounded-12 px-3 text-left transition-colors light:hover:bg-[rgba(0,0,0,0.04)]',
        access === 'ready' ? 'cursor-pointer' : 'cursor-default',
      )}
    >
      <UserAvatar userInfo={userInfo} className="size-8 shrink-0" />
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-1">
        <p className="truncate font-semibold light:text-black text-[14px] text-system-white leading-[18px]">
          {userInfo.nickname}
        </p>
        {access === 'ready' ? (
          <div className="flex items-center gap-1">
            {clawPass?.userVariant && (
              <UserTypeTag variant={clawPass.userVariant} className="light:bg-[rgba(87,101,242,0.16)]" />
            )}
          </div>
        ) : null}
      </div>
      {access === 'ready' && <BaseLineRight className="size-4 light:text-black/32 text-system-white36" />}
    </div>
  );

  if (access !== 'ready') {
    return triggerContent;
  }

  return (
    <HoverCard openDelay={80} closeDelay={120}>
      <HoverCardTrigger asChild={true}>{triggerContent}</HoverCardTrigger>
      <HoverCardContent
        side="right"
        align="end"
        sideOffset={12}
        className="w-auto rounded-12 border-none bg-transparent p-0 shadow-none"
      >
        <NavUserPopover userVariant={clawPass?.userVariant} />
      </HoverCardContent>
    </HoverCard>
  );
};

export default NavUserCard;
